import { Outlet, Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Container } from '@/components/ui/container';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { AppNav } from '@/components/ui/bottom-nav';
import { ConfirmAction } from '@/components/ui';
import { cn } from '@/lib/cn';
import { useSession, clearSession } from '@/lib/session';
import { useBadges } from '@/features/badges/api';
import { logoutRequest } from '@/features/auth/api';
import { useSyncFlavorTheme } from '@/features/profile/flavor-theme';
import { setCurrentSpot } from '@/features/spots/current';
import { GlowProvider } from '@/features/radar/glow-context';
import { GlowPill } from '@/features/radar/glow-pill';
import { Logo } from '@/components/ui/logo';

/** Chrome for the signed-in app: slim top bar; a bottom tab bar on phones/
 * tablets and a left rail on desktop (`lg+`). */
export function AppShell() {
  const { user } = useSession();
  const navigate = useNavigate();
  useSyncFlavorTheme();
  const { data: badges } = useBadges();

  // Thread routes (a single chat / room) run "immersive": the page fills the
  // space between the header and the tab bar exactly, and only its message list
  // scrolls. Everything else scrolls the whole `main`.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const immersive = /^\/app\/(whispers|pro|circles)\/[^/]+$/.test(pathname);

  const logout = async () => {
    await logoutRequest();
    clearSession();
    setCurrentSpot(null);
    void navigate({ to: '/' });
  };

  const suspendedUntil =
    user?.suspendedUntil && new Date(user.suspendedUntil) > new Date() ? user.suspendedUntil : null;
  const showPro = badges?.proActive ?? false;

  return (
    <GlowProvider>
      <div className="app-shell-root flex h-dvh flex-col overflow-x-clip overscroll-none">
        <header className="shrink-0 border-b-2 border-ink bg-canvas/80 backdrop-blur-md">
          <Container className="flex h-14 items-center justify-between gap-2">
            <Link to="/app" className="flex items-center gap-2 font-display font-semibold">
              <Logo className="size-4" />
              Nearmirer
            </Link>
            <div className="flex items-center gap-3">
              <GlowPill compact />
              {/* On desktop there's room for these; on mobile they live on /app/me. */}
              {user?.adminRole && (
                <Link
                  to="/admin"
                  className="hidden text-xs font-semibold text-ink-soft hover:text-ink sm:block"
                >
                  Admin
                </Link>
              )}
              <ThemeToggle />
              <ConfirmAction
                trigger={
                  <button className="hidden text-xs font-semibold text-ink-soft hover:text-ink sm:block">
                    Log out
                  </button>
                }
                title="Log out of Nearmirer?"
                body="You’ll need your email code to sign back in."
                confirmLabel="Log out"
                onConfirm={logout}
              />
            </div>
          </Container>
        </header>

        {suspendedUntil && (
          <div className="shrink-0 border-b-2 border-ink bg-warning/20">
            <Container className="py-2 text-center text-xs font-semibold">
              Your account is limited until {new Date(suspendedUntil).toLocaleDateString()}.{' '}
              <Link to="/app/appeal" className="underline">
                Appeal this decision
              </Link>
            </Container>
          </div>
        )}

        <div className="flex w-full min-h-0 flex-1">
          <aside className="hidden w-60 shrink-0 flex-col border-r-2 border-hairline px-3 py-6 lg:flex xl:w-64">
            <div className="mx-auto w-full max-w-[13rem]">
              <AppNav variant="rail" counts={badges} showPro={showPro} />
            </div>
          </aside>

          <main className="min-h-0 flex-1 overflow-y-auto">
            <Container
              className={cn(
                'mx-auto max-w-md lg:max-w-xl',
                immersive ? 'flex h-full flex-col py-0' : 'py-4',
              )}
            >
              <Outlet />
            </Container>
          </main>
        </div>

        <AppNav variant="bar" className="lg:hidden" counts={badges} showPro={showPro} />
      </div>
    </GlowProvider>
  );
}
